import React, { CSSProperties, Fragment, useEffect, useState } from 'react'
import { Switch, Route, useLocation, useHistory, Redirect } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import Confetti from 'react-confetti'

// Hooks
import useUser from 'Hooks/useUser'

import useOrganizationV2 from 'Hooks/useOrganizationV2'
import useBrand, { removeAnonymousSessionIdFromLocalStorage, saveAnonymousSessionIdToLocalStorage } from 'Hooks/useBrand'
import useAppView from 'Hooks/useAppView'
import useScreen from 'Hooks/useScreen'
import useMaestroChat from 'Hooks/useMaestroChat'
import { useNavbar } from 'Hooks/useNavbar'
import useSignupFlow from 'Hooks/useSignupFlow'
import usePlans from 'Hooks/usePlans'

// Components - Shared routes (work in both auth states)
import AssistantPage from 'pages/AssistantPage'
import MarketingCampaignDetails from 'pages/MarketingCampaignDetails/MarketingCampaignDetails'
import NotFoundPage from 'pages/NotFoundPage'
import Navbar from 'components/Navbar/Navbar'
import Footer from 'components/Footer/Footer'
import Pricing from 'pages/Pricing/Pricing'
import PublicRoute from 'components/PublicRoute'

// Components - Auth-protected routes
import SettingsPage from 'pages/SettingsPage/SettingsPage'
import WebsiteHomepage from 'pages/WebsitePage/Website'
import Dashboard from 'pages/DashboardPage/DashboardPage'
import MarketingPage from 'pages/MarketingPage/MarketingPage'
import OrganizationHomePage from 'pages/OrganizationHomePage'
import Fans from 'pages/FansPage/Fans'
import CreateNewSymphonyProfile from 'pages/AddArtist/CreateNewSymphonyProfile'
import MarketingSuccessPage from 'pages/MarketingPage/SuccessPage'
import PreSaveProvider from 'pages/MarketingPage/Data/PreSaveProvider'
import PreSave from 'pages/MarketingPage/PreSave/PreSave'
import VideoViews from 'pages/MarketingPage/VideoAds/VideoViews'
import EnterEmail from 'pages/EnterEmail/EnterEmail'
import ReferPage from 'pages/Refer'
import AdminVideoViewsDataUpload from 'pages/Admin/VideoViewsDataUpload'
import StripeSubscriptionSync from 'pages/Admin/StripeSubscriptionSync'
import InvoiceDownloader from 'pages/Admin/InvoiceDownloader'
import CampaignReports from 'pages/Admin/reports'
import Learn from 'pages/LearnPage/Learn'
import KPITrends from 'pages/Admin/kpis'
import AffiliatesDashboard from 'pages/Admin/AffiliatesDashboard'
import SourcesTrends from 'pages/Admin/sources'
import TopPerforming from 'pages/Admin/TopPerforming'
import AgentCampaignsPage from 'pages/AgentCampaignsPage'
import UploadEmailsByCsv from 'pages/FansPage/Components/UploadEmailsByCsv/UploadEmailsByCsv'
import AdminOnboarding from 'pages/OrganizationPage/componentsOrganization/AdminOnboarding'
import ElementsPage from 'pages/ElementsPage'
import TestApiPage from 'pages/TestApiPage'
import TiktokCallbackPage from 'pages/TikTokLoginCallback'
import CustomerSupportBrandEditingTools from 'pages/CustomerSupportBrandEditingTools'
import MessageYourFans from 'pages/FansPage/Components/MessageYourFans/MessageYourFans'
import EmailDetailsV2 from 'pages/EmailDetailsV2'
import TablePage from 'pages/TablePage'
import Clipper from 'pages/Admin/Clipper'
import MarketingFlow2 from 'components/MarketingFlow2'
import CampaignSubmissionView from 'components/CampaignSubmissionView'
import WebsiteBuilder from 'components/websiteBuilder/WebsiteBuilder'
import WebsitePage from 'pages/WebsitePage/components/WebsitePage'
import AllWebsites from 'pages/AllWebsites'
import AddArtistV2 from 'pages/AddArtistV2'
import AddBrandV3 from 'pages/AddBrandV3'
import TeamSetupPage from 'pages/TeamSetupPage'
import TeamConnectionsPage from 'pages/TeamConnectionsPage'
import TeamUsersPage from 'pages/TeamUsersPage'
import ApiKeysPage from 'pages/ApiKeyPage'
import CampaignsDashboard from 'pages/CampaignsDashboard'
import Unsubscribe from 'pages/Unsubscribe'
import TeamTrialBanner from 'components/shareable/TeamTrialBanner'
import TeamTrialExpiredModal from 'components/shareable/TeamTrialExpiredModal'

// Providers and Modals
import UpgradeModalProvider from 'Hooks/UpgradeModalContext'
import NavbarProvider from 'pages/MarketingPage/Data/NavbarProvider'
import PaymentPastDueModal from 'components/shareable/PaymentPastDueModal'
import GlobalModalRenderer from 'components/shareable/GlobalModalRenderer/GlobalModalRenderer'
import AdminTeamViewBanner from 'components/AdminTeamViewBanner/AdminTeamViewBanner'
import StickyBanner from 'elements/StickyBanner'

// UI Components
import LoaderOverlay from 'elements/LoaderOverlay'

// Utils and Constants
import { NAV_BAR_HEIGHT_MOBILE, PlaybookType, ExternalSiteLinks, TEAM_SETUP_PATH } from 'modules/Const'
import { styx } from 'modules/Utils'
import { CurrentBrand, OrganizationInvitationStatus, OrganizationStatus } from 'modules/Types'
import Logger from 'Logger'
import Axios from 'helpers/Interceptor'
import Colors from 'modules/Colors'
import { scrollToTop } from 'helpers/StyleUtils'
import MembersPage from 'pages/SettingsPage/Components/MembersPage'
import OrganizationPaymentPastDueModal from 'components/shareable/OrganizationPaymentPastDueModal'

// Simple debug component to test route matching
const DebugAddArtistComponent = () => {
    return (
        <div style={{
            background: 'red',
            color: 'white',
            padding: 20,
            margin: 20,
            fontSize: 18,
            textAlign: 'center'
        }}>
            DEBUG: /add-artist route is working! AddArtistV2 should load here.
        </div>
    );
};

// Helper function to automatically generate external routes from ExternalSiteLinks
const generateExternalRoutesFromEnum = (): Record<string, string> => {
    const routes: Record<string, string> = {}

    // Iterate through all ExternalSiteLinks entries
    Object.values(ExternalSiteLinks).forEach(url => {
        if (url.includes('site.symphonyos.co')) {
            try {
                const urlObj = new URL(url)
                const pathname = urlObj.pathname
                routes[pathname] = url
            } catch (error) {
                console.warn(`Invalid URL in ExternalSiteLinks: ${url}`)
            }
        }
    })

    // Add manual mappings for non-site.symphonyos.co URLs or special cases
    routes['/help-center'] = ExternalSiteLinks.HELP_CENTER
    routes['/video-walkthroughs'] = ExternalSiteLinks.SOCIAL_YOUTUBE
    // Map the long fan-insights path to the shorter solutions path
    routes['/solutions/fan-insights'] = ExternalSiteLinks.SOLUTIONS_CROSS_PLATFORM_ANALYTICS

    return routes
}

// External routes that redirect to external sites - automatically generated from ExternalSiteLinks
const externalRoutes: Record<string, string> = generateExternalRoutesFromEnum()

// Helper function to check if a route should redirect externally
const checkIsExternalRoute = (pathname: string): string | null => {
    // Check exact matches first
    if (externalRoutes[pathname]) {
        return externalRoutes[pathname]
    }

    // Check for partnership patterns
    const partnershipsPatterns = ['/partners/', '/partnerships']
    const shouldRedirectToOldSite = partnershipsPatterns.some(pattern => pathname.includes(pattern))

    if (shouldRedirectToOldSite) {
        // Use the base URL from our partnerships enum entry
        const baseUrl = ExternalSiteLinks.PARTNERSHIPS.replace('/partnerships', '')
        return `${baseUrl}${pathname}`
    }

    return null
}

// Note: Route protection is handled individually by wrapping routes with AuthProtectedRoute component

/**
 * Component that handles auth-protected routes with redirect logic
 */
const AuthProtectedRoute: React.FC<{
    children: React.ReactNode;
    isAdminRoute?: boolean;
}> = ({ children, isAdminRoute = false }) => {
    const { isAuthenticated, loginWithRedirect } = useAuth0()
    const { isSymphonyAdmin } = useUser()
    const location = useLocation()

    // Check for token in URL and save it before redirect
    React.useEffect(() => {
        if (!isAuthenticated) {
            const params = new URLSearchParams(location.search)
            const token = params.get('token')

            if (token) {
                saveAnonymousSessionIdToLocalStorage(token)
                console.log('📧 Saved email campaign token to anonymous-session-id:', token)
            }
        }
    }, [isAuthenticated, location.search])

    if (!isAuthenticated) {
        loginWithRedirect({
            appState: {
                returnTo: location.pathname + location.search
            }
        })
        return renderWelcomeToSymphonyLoaderOverlay('Redirecting to login...')
    }

    // Redirect non-admin users away from admin routes
    if (isAdminRoute && !isSymphonyAdmin) {
        return <Redirect to="/dashboard" />
    }

    return <>{children}</>
}

const renderWelcomeToSymphonyLoaderOverlay = (text?: string) => {
    return (
        (
            <LoaderOverlay
                className="loaderFullScreenInitializeApp"
                title=""
            >
                <div
                    className="welcomeToSymphony"
                    style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 20,
                        marginTop: 10,
                    }}
                >
                    {text || 'Welcome to Symphony'}
                </div>
            </LoaderOverlay>
        )
    )
}

/**
 * Component that initializes user session data for authenticated users
 */
const InitializationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const history = useHistory()
    const {
        isAuthenticated: isAuthenticatedWithAuth0,
        isLoading: isAuth0Loading,
        logout,
        getAccessTokenSilently,
        user: userAuth0,
    } = useAuth0()

    const {
        currentUser,
        getCurrentUser,
    } = useUser()

    const {
        currentBrand,
        getBrands,
        loadedBrands,
    } = useBrand()

    const {
        organization,
        isOrganizationInitialized,
        getOrganization: getOrganizationV2,
        setIsOrganizationInitialized,
        setOrganization
    } = useOrganizationV2()
    const { initializeAppView } = useAppView.getState();

    const { handlePostAuthFlow } = useSignupFlow()

    /** Initializes the user session with Auth0 and database details */
    const initializeUserSession = async () => {
        try {
            // Wait for Auth0 to finish loading before making any decisions
            if (isAuth0Loading) {
                return
            }

            // This component should only be rendered for authenticated users
            if (!isAuthenticatedWithAuth0) {
                console.error('InitializationProvider rendered for unauthenticated user')
                return
            }

            // Add guards against re-initialization
            const userSubAuth0 = userAuth0?.sub
            if (!userSubAuth0) {
                return
            }

            // Handle TikTok auth code if present
            const params = new URLSearchParams(window.location.search)
            const authCode = params.get('auth_code')
            if (authCode) {
                localStorage.setItem('tt_auth_code', authCode)
            }

            const currentUserLocal = await getCurrentUser(
                true,
                userSubAuth0,
                getAccessTokenSilently,
                logout,
            )

            // Initialize organization data first - get user's organizations directly (supports TEAM_V2 without brands)
            // TODO: This should be renamed to getOrganizations to support multiple teams per user
            const organizationData = await getOrganizationV2({
                userId: currentUserLocal?.id,
            })

            const organizationId = organizationData?.organization.id
            const hasOrganization = organizationData && organizationId

            // Load brands - enhanced getBrands now includes organization brands based on user roles
            // This ensures admins see all organization brands, members see their assigned brands
            const shouldSetCurrentBrand = !hasOrganization;
            const brandsResult = await getBrands(shouldSetCurrentBrand, true);
            const currentBrandLocal = shouldSetCurrentBrand ? brandsResult as CurrentBrand : null;

            if (!currentBrandLocal?.id && !hasOrganization) {
                // Check for post-authentication flow (anonymous brand linking, temporary brand migration, etc.)
                // Only for users without organizations
                await handlePostAuthFlow(currentUserLocal, {
                    history
                })
            }

            if (hasOrganization) {
                const organizationUsersLocal = organizationData?.organizationUsers
                const invitationStatusLocal = organizationUsersLocal?.find(ou => ou.user_id === currentUserLocal?.id)?.invitation_status
                const organizationSetupStatus = organizationData.organization.setup_status

                // Redirect to team setup for pending invitations
                if (invitationStatusLocal === OrganizationInvitationStatus.PENDING) {
                    history.push(TEAM_SETUP_PATH)
                }
                // Redirect to team setup for organizations needing setup (TEAM_V2 flow)
                else if (organizationSetupStatus === OrganizationStatus.WAITING_FOR_DETAILS) {
                    history.push(TEAM_SETUP_PATH)
                }
            } else {
                // For users without organizations, manually set organization as initialized
                // to prevent the loading overlay from persisting
                setOrganization(undefined)
                setIsOrganizationInitialized(true)
            }

            // Initialize app view based on user's organization and brand status
            initializeAppView();
        } catch (err) {
            Logger.error('Error initializing session:', err)
            // For authenticated users, if there's an error, we should logout
            logout()
        }
    }

    useEffect(() => {
        if (!isAuth0Loading) {
            initializeUserSession()
        }
    }, [isAuth0Loading, isAuthenticatedWithAuth0, logout])

    useEffect(() => {
        if (!currentUser || !loadedBrands || !isOrganizationInitialized) {
            return // Wait for full initialization before any redirects
        }

        if (!currentUser.email) {
            history.push('/email')
            return
        }

        // Check for signup flow context that should skip add-artist redirect
        const hasPreAuthData = localStorage.getItem('pre-auth-chat-id') ||
            localStorage.getItem('pre-auth-campaign-id') ||
            localStorage.getItem('anonymous-session-id') // Use anonymous session ID directly

        if (!loadedBrands.length && !hasPreAuthData) {
            history.push('/add-artist')
            return
        }

        if (organization?.setup_status === OrganizationStatus.WAITING_FOR_DETAILS) {
            history.push(TEAM_SETUP_PATH)
            return
        }
    }, [currentUser, loadedBrands, organization, isOrganizationInitialized])

    // Show loading until initialization is complete
    if (!currentUser || !loadedBrands || !isOrganizationInitialized) {
        return renderWelcomeToSymphonyLoaderOverlay()
    }

    return <div
        className="initializeAppComponent"
        style={styx(sty.initializeAppComponent)}
    >{children}</div>
}

/**
 * Main App component that handles all routing and authentication states
 */
const MainAppRouter = () => {
    const location = useLocation()
    const { isAuthenticated, isLoading } = useAuth0()
    const { mStyle, mobileView, screenWidth, screenHeight } = useScreen()
    const {
        isWebsiteChildPage
    } = useNavbar()
    const { currentBrand } = useBrand()
    const { organization } = useOrganizationV2()
    const { isTeamView } = useAppView()
    const { isChatOpen } = useMaestroChat()
    const { trackReferralFromUrl, hasReferral } = useUser()

    // Initialize plans for both authenticated and unauthenticated users
    const { fetchPlans, isInitialized } = usePlans()

    // Referral and confetti state
    const [showConfetti, setShowConfetti] = useState(false)
    const [hasReferralState, setHasReferralState] = useState(false)


    // Handle external route redirects and referral tracking
    useEffect(() => {
        const { pathname, search } = location
        const externalUrl = checkIsExternalRoute(pathname)

        if (externalUrl) {
            // Add search params if they exist
            const fullUrl = search ? `${externalUrl}${search}` : externalUrl
            window.location.href = fullUrl
        }

        // Track referral parameters from URL (works for all routes and auth states)
        trackReferralFromUrl(pathname + search)
    }, [location])

    // Scroll to top when route changes
    useEffect(() => {
        scrollToTop()
    }, [location.pathname])

    // Initialize plans for unauthenticated users only
    // Authenticated users get their plans fetched in InitializationProvider with their brand's currency
    // This ensures pricing data is available on public pages like /pricing
    useEffect(() => {
        // Only fetch if not already initialized and not currently loading
        if (!isInitialized() && !isLoading) {
            fetchPlans({
                currency: 'USD', // Default currency for unauthenticated users
                useCache: true
            }).catch(error => {
                console.error('Failed to fetch plans for public access:', error)
            })
        }
    }, [isLoading, isInitialized])

    // Handle referral detection and confetti
    useEffect(() => {
        // Check if user came from a referral
        const referralStatus = hasReferral()
        const showReferralUI = referralStatus.inURL
        setHasReferralState(showReferralUI)

        // Show confetti for active referrals (in URL)
        if (showReferralUI) {
            setShowConfetti(true)
            // Stop confetti after 5 seconds
            setTimeout(() => setShowConfetti(false), 5000)
        }
    }, [hasReferral])

    // Show loading during Auth0 initialization or callback processing
    const isAuth0Callback = location.search.includes('code=') || location.search.includes('state=')
    if ((!isAuthenticated && isAuth0Callback)) {
        return renderWelcomeToSymphonyLoaderOverlay()
    }

    // Determine overflow behavior for website child pages
    const overflowX = isWebsiteChildPage() ? 'visible' : 'hidden'

    // Payment modals are handled via PaymentPastDueModal with autoShow={true}

    const getFlexDirection = () => {
        if (isAuthenticated) {
            return 'row'
        }

        return 'column'
    }


    const renderContent = () => {
        return (
            <div
                className="mainAppContent fade-in"
                style={styx(
                    sty.mainAppContent,
                    { flexDirection: getFlexDirection() }
                )}
            >
                <div style={styx(sty.stickyNavbar)}>
                    <Navbar className="navbarFromMainApp" />
                </div>



                <main
                    className="mainAppRoutesContainer"
                    style={styx(
                        mStyle(sty, 'mainAppRoutesContainer'),
                        { overflowX: overflowX as CSSProperties['overflowX'] },
                    )}
                >
                    <Switch>
                        {/* Team Dashboard Route - Protected route for organization view */}
                        <Route exact path="/team/dashboard">
                            <AuthProtectedRoute>
                                <OrganizationHomePage />
                            </AuthProtectedRoute>
                        </Route>

                        {/* Home route - Maestro Assistant for everyone */}
                        <Route exact path="/">
                            {isAuthenticated && isTeamView() ? (
                                // Redirect team users to team dashboard
                                <Redirect to="/team/dashboard" />
                            ) : (
                                // Show Maestro for brand users and unauthenticated users
                                <PublicRoute pageName="Homepage">
                                    <AssistantPage isPublic={!isAuthenticated} />
                                </PublicRoute>
                            )}
                        </Route>

                        {/* Public Assistant route */}
                        <Route exact path={['/assistant', '/join']}>
                            <PublicRoute pageName="Assistant">
                                <AssistantPage isPublic={!isAuthenticated} />
                            </PublicRoute>
                        </Route>

                        {/* Authenticated chat route */}
                        <Route exact path="/chats/:chatId">
                            <AuthProtectedRoute>
                                <AssistantPage isPublic={false} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/pricing">
                            <PublicRoute pageName="Pricing">
                                <Pricing />
                            </PublicRoute>
                        </Route>

                        <Route exact path="/unsubscribe">
                            <PublicRoute pageName="Unsubscribe">
                                <Unsubscribe />
                            </PublicRoute>
                        </Route>
                        {/* Auth-protected routes */}
                        <Route exact path="/setup">
                            <AuthProtectedRoute>
                                <AddBrandV3 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing2/new/:playbookType">
                            <AuthProtectedRoute>
                                <MarketingFlow2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing2/draft/:draftId">
                            <AuthProtectedRoute>
                                <MarketingFlow2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing2/submission/:campaignId">
                            <AuthProtectedRoute>
                                <CampaignSubmissionView />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/email">
                            <AuthProtectedRoute>
                                <EnterEmail />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing" >
                            <AuthProtectedRoute>
                                <MarketingPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/dashboard">
                            <AuthProtectedRoute>
                                <Dashboard />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/campaigns/:campaignId">
                            <AuthProtectedRoute>
                                <MarketingCampaignDetails className="marketingCampaignDetailsComponentPostAuth" />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/presave">
                            <AuthProtectedRoute>
                                <PreSaveProvider>
                                    <PreSave setGoal={() => null} />
                                </PreSaveProvider>
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/music-streams">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.INCREASE_MUSIC_STREAMS} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/grow-playlist-followers">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.GROW_PLAYLIST_FOLLOWERS} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/grow-instagram-engagement">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/playlists">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.GET_PLAYLISTED} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/video-views">
                            <AuthProtectedRoute>
                                <VideoViews />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/tiktok-ads">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.TIKTOK_BOOST_ENGAGEMENT} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/link-clicks">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.INCREASE_LINK_CLICKS} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/add-artist-OLD">
                            <AuthProtectedRoute>
                                <CreateNewSymphonyProfile />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/add-artist">
                            <AuthProtectedRoute>
                                <AddArtistV2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/goals">
                            <AuthProtectedRoute>
                                <DebugAddArtistComponent />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/marketing/success">
                            <AuthProtectedRoute>
                                <MarketingSuccessPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/tiktok/callback">
                            <AuthProtectedRoute>
                                <TiktokCallbackPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/website">
                            <AuthProtectedRoute>
                                <WebsitePage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/website-builder">
                            <AuthProtectedRoute>
                                <WebsiteBuilder />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/website-builder/:brandSlug/:websiteSlug">
                            <AuthProtectedRoute>
                                <WebsiteBuilder />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/fans/campaigns/:id">
                            <AuthProtectedRoute>
                                <EmailDetailsV2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/fans">
                            <AuthProtectedRoute>
                                <Fans />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/import-fans">
                            <AuthProtectedRoute>
                                <UploadEmailsByCsv />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/message-your-fans">
                            <AuthProtectedRoute>
                                <MessageYourFans />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/website/:websiteType/:websiteSlug">
                            <AuthProtectedRoute>
                                <WebsiteHomepage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/elementspage">
                            <AuthProtectedRoute>
                                <ElementsPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/testapi">
                            <AuthProtectedRoute>
                                <TestApiPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admintools">
                            <AuthProtectedRoute>
                                <CustomerSupportBrandEditingTools />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/allwebsites">
                            <AuthProtectedRoute>
                                <AllWebsites />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/learn">
                            <AuthProtectedRoute>
                                <Learn />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/settings">
                            <AuthProtectedRoute>
                                <SettingsPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path={TEAM_SETUP_PATH}>
                            <AuthProtectedRoute>
                                <TeamSetupPage />
                            </AuthProtectedRoute>
                        </Route>


                        <Route exact path="/users">
                            <AuthProtectedRoute>
                                <MembersPage className="membersPage" />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/team/connections">
                            <AuthProtectedRoute>
                                <TeamConnectionsPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/team/users">
                            <AuthProtectedRoute>
                                <TeamUsersPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/onboarding">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <AdminOnboarding />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/refer">
                            <AuthProtectedRoute>
                                <ReferPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/video-views-data-upload">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <AdminVideoViewsDataUpload />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/reports">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <CampaignReports />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/top-performing">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <TopPerforming />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/kpis">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <KPITrends />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/sources">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <SourcesTrends />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/affiliates">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <AffiliatesDashboard />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/agent-campaigns">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <AgentCampaignsPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/stripe-sync">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <StripeSubscriptionSync />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/invoice-downloader">
                            <AuthProtectedRoute isAdminRoute={true}>
                                <InvoiceDownloader />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/tools/clipper">
                            <AuthProtectedRoute>
                                <Clipper />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/table">
                            <AuthProtectedRoute>
                                <TablePage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/api-keys">
                            <AuthProtectedRoute>
                                <ApiKeysPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/campaigns-dashboard">
                            <AuthProtectedRoute>
                                <CampaignsDashboard />
                            </AuthProtectedRoute>
                        </Route>

                        {/* Catch-all route */}
                        <Route path="*">
                            <PublicRoute pageName="404 Not Found">
                                <NotFoundPage isPublic={!isAuthenticated} />
                            </PublicRoute>
                        </Route>
                    </Switch>
                </main>

                {/* Footer for public routes when chat is not open - now outside main content for natural flow */}
                {!isAuthenticated && !isChatOpen && (
                    <Footer />
                )}
            </div>
        )
    }

    return (
        <Fragment>
            {/* Confetti effect for referrals */}
            {showConfetti && (
                <Confetti
                    width={screenWidth}
                    height={screenHeight}
                    recycle={false}
                    numberOfPieces={400}
                    gravity={0.3}
                />
            )}

            {/* Referral message - only show if user has active referral (in URL) and is not authenticated */}
            <StickyBanner
                show={hasReferralState && !isAuthenticated}
                backgroundColor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                textColor="white"
                borderColor="rgba(255,255,255,0.1)"
                icon="🎉"
                mainText="Your friend has referred you to Symphony! Let's get you started."
                style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    position: 'relative'
                }}
            />

            {/* Admin Team View Banner - shows when admin is viewing a team */}
            <AdminTeamViewBanner />

            {/* Team View Rollout Banner - shows for authenticated users with organization */}
            <StickyBanner
                show={isAuthenticated && !!organization}
                backgroundColor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                textColor="white"
                borderColor="rgba(255,255,255,0.1)"
                mainText="We're rolling out a new and redesigned Team View today, so you may notice a few issues as you're using the platform today. We're working through cleaning things up, but if you notice anything - please reach out to our team!"
                style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    position: 'relative'
                }}
                primaryButton={{
                    text: 'Contact Support',
                    onClick: () => window.open('mailto:megh@symphony.to,team@symphony.to', '_blank'),
                    variant: 'outlined',
                    color: 'yellow',
                    size: 'small',
                    style: {
                        borderColor: '#FFC107',
                        color: '#856404',
                        backgroundColor: 'white'
                    }
                }}
            />

            {/* <div className="mainApp" style={styx(sty.mainApp)}> */}
            {/* UpgradeModalProvider wraps everything so it's available for both auth states */}
            <UpgradeModalProvider>
                {isAuthenticated ? (
                    // Authenticated users get full initialization and providers
                    <InitializationProvider>
                        <NavbarProvider>
                            {renderContent()}

                            {/* Auth-dependent modals */}
                            <PaymentPastDueModal autoShow={true} />
                            <OrganizationPaymentPastDueModal autoShow={true} />

                            {/* Team Trial Management */}
                            <TeamTrialBanner />
                            <TeamTrialExpiredModal />

                            {/* Global Modal System - Centralized rendering for ALL modals */}
                            <GlobalModalRenderer />
                        </NavbarProvider>
                    </InitializationProvider>
                ) : (
                    // Unauthenticated users get minimal providers
                    renderContent()
                )}
            </UpgradeModalProvider>
        </Fragment>
    )
}

const sty: Record<string, CSSProperties> = {
    mainApp: {
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#181818',
        backgroundImage: 'url(https://cdn.prod.website-files.com/61fc721130a4089acadae508/6685b3d152d239f14cf16fb7_bg.svg)',
        backgroundPosition: '50%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto',
        backgroundAttachment: 'fixed',
    },
    mainAppContent: {
        display: 'flex',
        width: '100%',
        minHeight: '100dvh',
        flex: 1,
    },
    stickyNavbar: {
        position: 'sticky',
        top: 0,
        zIndex: 1000,
    },

    mainAppRoutesContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        backgroundColor: Colors.greyLighter,
        minHeight: '100%',
    },
    mainAppRoutesContainer_mobile: {
        marginLeft: 0,
    },
    initializeAppComponent: {
        display: "flex",
        width: "100%",
    }
}


export default MainAppRouter 